import React from 'react'
import { t } from 'ttag'

import { Box } from 'components/layout/Box/Box'
import { Button } from 'components/shared/Button/Button'
import { Detail } from 'components/shared/Detail/Detail'
import { IconButton } from 'components/shared/IconButton/IconButton'
import { Pane } from 'components/shared/Pane/Pane'
import { RequireRole } from 'components/auth/features'
import {
  getProfileCompetenciesLabels,
  getLanguageLabels,
} from 'components/modals/PositionTemplateModal/common/TemplateDetails/model'

import {
  Edit3Icon,
  MessageCircleIcon,
  PlusCircleIcon,
} from 'components/shared/Icon/model'

import styles from './JobDetailsContent.module.scss'

type JobDetailsContentProps = {
  department?: any
  description?: string
  interview_template?: any
  language?: string
  languages?: string[]
  name?: string
  profile?: any
  onAddJob: () => void
  onBack: () => void
  onEdit: () => void
  hasCategories?: boolean
}

export const JobDetailsContent = ({
  department,
  description,
  interview_template,
  language,
  languages,
  name,
  profile,
  onAddJob,
  onBack,
  onEdit,
  hasCategories,
}: JobDetailsContentProps) => (
  <>
    <Pane.Header title={name} {...{ onBack }}>
      <Box className={styles.actions} justify="flex-end">
        <RequireRole role="accountAdmin">
          <IconButton
            icon={Edit3Icon}
            size="small"
            variant="text"
            onClick={onEdit}
          />
        </RequireRole>
        <Button
          className={styles.button}
          color="primary"
          icon={PlusCircleIcon}
          label={t`Add job`}
          size="small"
          variant="filled"
          onClick={onAddJob}
        />
      </Box>
    </Pane.Header>
    <Box className={styles.content}>
      <Box className={styles.main}>
        <Detail>
          <Detail.LabelWithTooltip>{t`Name`}</Detail.LabelWithTooltip>
          <Detail.Content type="text">{name}</Detail.Content>
        </Detail>
        <Detail>
          <Detail.LabelWithTooltip>{t`Department`}</Detail.LabelWithTooltip>
          <Detail.Content type="text">{department?.name}</Detail.Content>
        </Detail>
        {languages ? (
          <Detail>
            <Detail.LabelWithTooltip
              tooltip={t`Selected language(s) will be available as options for candidate invitation and test language.`}
            >{t`Languages`}</Detail.LabelWithTooltip>
            <Detail.Content type="text">
              {getLanguageLabels(languages)}
            </Detail.Content>
          </Detail>
        ) : null}
        {interview_template ? (
          <Detail>
            <Detail.LabelWithTooltip
              tooltip={t`The job template has an interview template connected to it.`}
            >
              {t`Interview`}
            </Detail.LabelWithTooltip>
            <Detail.Content icon={MessageCircleIcon} type="iconWithText">
              {interview_template}
            </Detail.Content>
          </Detail>
        ) : null}
        {hasCategories ? (
          <Detail>
            <Detail.LabelWithTooltip
              tooltip={t`The selected competencies that are most relevant to the job requirements. They determine how the calculation of test scores will be adjusted to favour relevant personality factors while the content of the candidate's tests remains unchanged.`}
            >
              {t`Most fitting competencies`}
            </Detail.LabelWithTooltip>
            <Detail.Content
              chips={getProfileCompetenciesLabels(profile, language)}
              type="chipsGroup"
            />
          </Detail>
        ) : null}
        {description ? (
          <Detail>
            <Detail.LabelWithTooltip>{t`Description`}</Detail.LabelWithTooltip>
            <Detail.Content type="text">{description}</Detail.Content>
          </Detail>
        ) : null}
      </Box>
    </Box>
  </>
)
